import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge } from '@components';
import { DeliveryLog } from '../types';

interface DeliveryLogTableProps {
  logs: DeliveryLog[];
  onViewLog: (id: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

export const DeliveryLogTable: React.FC<DeliveryLogTableProps> = ({
  logs,
  onViewLog,
  page,
  pageSize,
  totalCount,
  onPageChange,
  loading = false,
}) => {
  const { t } = useTranslation();
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getTypeVariant = (type: string) => {
    switch (type) {
      case 'email':
        return 'info';
      case 'sms':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatTimestamp = (log: DeliveryLog) => {
    const dateString = log.created || log.sent_at;
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.timestamp')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.type')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.recipient')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.template')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('common.status')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {formatTimestamp(log)}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={getTypeVariant(log.delivery_type)} size="sm">
                        {log.delivery_type.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{log.recipient}</td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {log.template || log.template_name || 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={getStatusVariant(log.status)} size="sm">
                        {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewLog(log.id)}
                        className="p-1 h-8 w-8"
                        title="View Log"
                      >
                        <span className="material-icons text-lg">visibility</span>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalCount > pageSize && (
          <div className="flex items-center justify-between mt-4 px-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1}
                title="Previous"
                className="h-8 w-8 p-0"
              >
                <span className="material-icons text-base">chevron_left</span>
              </Button>

              {(() => {
                const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
                const pages: (number | 'ellipsis')[] = [];
                const add = (n: number) => {
                  if (!pages.includes(n)) pages.push(n);
                };
                add(1);
                add(totalPages);
                add(page);
                add(page - 1);
                add(page + 1);
                const normalized = Array.from(
                  new Set(
                    pages
                      .filter(p => typeof p === 'number' && p >= 1 && (p as number) <= totalPages)
                      .sort((a, b) => (a as number) - (b as number))
                  )
                );
                const withEllipsis: (number | 'ellipsis')[] = [];
                for (let i = 0; i < normalized.length; i++) {
                  const current = normalized[i] as number;
                  const prev = normalized[i - 1] as number | undefined;
                  if (prev !== undefined && current - prev > 1) {
                    withEllipsis.push('ellipsis');
                  }
                  withEllipsis.push(current);
                }
                return withEllipsis.map((p, idx) =>
                  p === 'ellipsis' ? (
                    <span
                      key={`e-${idx}`}
                      className="px-2 text-sm text-gray-500 dark:text-gray-400"
                    >
                      …
                    </span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === page ? 'light' : 'gradient-multi'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => onPageChange(p as number)}
                    >
                      {p}
                    </Button>
                  )
                );
              })()}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(page * pageSize < totalCount ? page + 1 : page)}
                disabled={page * pageSize >= totalCount}
                title="Next"
                className="h-8 w-8 p-0"
              >
                <span className="material-icons text-base">chevron_right</span>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
